import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Star, Quote, Play } from "lucide-react";

export default function TestimonialsSection() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="mt-14" data-testid="testimonials-section">
      <div className="flex items-center gap-2 mb-4">
        <Quote size={18} className="text-[#0055FF]" />
        <h2 className="font-heading font-semibold text-2xl tracking-tight">Real client wins</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <TestimonialCard key={t.id} t={t} />
        ))}
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  const [showVideo, setShowVideo] = useState(false);
  const hasVideo = !!t.video_url;
  const gallery = (t.images || []).filter(Boolean);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden hover:border-[#0055FF] transition-colors flex flex-col" data-testid={`testimonial-${t.id}`}>
      {hasVideo && showVideo && (
        <div className="aspect-video bg-black" data-testid={`testimonial-video-player-${t.id}`}>
          <video src={t.video_url} controls autoPlay className="w-full h-full" />
        </div>
      )}
      {hasVideo && !showVideo && (
        <button
          type="button"
          onClick={() => setShowVideo(true)}
          className="aspect-video bg-[#09090B] relative group flex items-center justify-center overflow-hidden"
          data-testid={`testimonial-play-${t.id}`}
        >
          {gallery[0] || t.photo_url ? (
            <img src={gallery[0] || t.photo_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" loading="lazy" decoding="async" />
          ) : null}
          <div className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
            <Play size={22} className="text-[#0055FF] fill-[#0055FF] ml-0.5" />
          </div>
          <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider font-semibold text-white bg-black/60 px-2 py-0.5 rounded-sm">Video story</span>
        </button>
      )}

      {!hasVideo && gallery.length > 0 && (
        <div className="aspect-video bg-[#F4F4F5] grid grid-cols-3 gap-0.5">
          {gallery.slice(0, 3).map((src, i) => (
            <img key={`${src}-${i}`} src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          ))}
        </div>
      )}

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          {t.photo_url ? (
            <img src={t.photo_url} alt={t.client_name} className="w-12 h-12 rounded-full object-cover border border-[#E4E4E7]" loading="lazy" decoding="async" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#EEF4FF] flex items-center justify-center text-[#0055FF] text-lg font-bold">{t.client_name?.[0]?.toUpperCase() || "?"}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-[14px] text-[#09090B]">{t.client_name}</div>
            {(t.deal_summary || t.location) && (
              <div className="text-[11px] text-[#52525B]">
                {t.deal_summary}{t.deal_summary && t.location ? " · " : ""}{t.location}
              </div>
            )}
            <div className="flex items-center gap-0.5 mt-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={11} className={i < (t.rating || 0) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D4D4D8]"} />
              ))}
            </div>
          </div>
        </div>
        <p className="text-[13px] text-[#09090B] mt-3 italic leading-relaxed flex-1">"{t.quote}"</p>
        {gallery.length > 3 && (
          <div className="text-[10px] text-[#A1A1AA] mt-2">+{gallery.length - 3} more photos</div>
        )}
      </div>
    </div>
  );
}
